/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, FilterModel, GridApi, GridReadyEvent, IServerSideDatasource } from '@ag-grid-community/core';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { EntityFns, UnitOfWork } from '@data';
import { ActiveStatusEnum, ChangeLog, LogTypeEnum } from '@models';
import { AgFns, GridOptionsExt, ISortModel } from '../ag-grid';
import { Entity } from 'breeze-client';
import { firstValueFrom } from 'rxjs';

/** One row of BaseController.ChangeLogLabels. The server serializes PascalCase. */
interface ChangeLogLabel {
  TypeName: string;
  Id: string;
  Label: string;
}

@Component({
  selector: 'prox-changelog-grid',
  template: `<div class="h-full">
    <prox-ag-grid [gridOptions]="gridOptions"></prox-ag-grid>
  </div>`,
  standalone: false,
  styles: [
    `
      /* Action chips. Translucent BACKGROUNDS only, never a color rule: a bare
         '.cl-x { color }' is 0,1,0 and loses to the QB theme's
         '.ag-theme-alpine .ag-cell' at 0,2,0, so the text would silently stay default.
         Letting the theme's own color show through also keeps these readable in dark mode. */
      :host ::ng-deep .cl-add {
        background-color: rgba(34, 197, 94, 0.22);
      }
      :host ::ng-deep .cl-update {
        background-color: rgba(59, 130, 246, 0.2);
      }
      :host ::ng-deep .cl-delete {
        background-color: rgba(239, 68, 68, 0.24);
      }
      /* A subject that could not be resolved - dimmed so the eye skips it rather than
         reading a raw id as meaningful. */
      :host ::ng-deep .cl-unresolved {
        opacity: 0.45;
        font-style: italic;
      }
    `,
  ],
})
export class ChangeLogGridComponent implements OnInit, OnChanges {
  @Input() uow!: UnitOfWork;
  @Input() filterModel?: FilterModel;
  @Input() entity?: Entity;
  @Input() entityId?: string;
  /** Ask for a whole account's history rather than one row's. Set by the account form, where
   *  "Change Log" means everything that happened on the account - its programs, user groups,
   *  allowances, budgets and orders - not the handful of edits made to the Accounts row itself.
   *  Takes precedence over `entity`/`entityId`; the server stamps ChangeLog.AccountId at save
   *  time (AccountScopeResolver) so this is one indexed column, not a join. */
  @Input() accountId?: string;
  /** Read the WHOLE table, every account. Only the global admin page sets this.
   *  It exists so that "no scope given" can mean "no rows" rather than "everything": an unset
   *  accountId used to silently drop the where clause and show one account's form the audit
   *  trail of every other account. Showing everything must be asked for, never defaulted to. */
  @Input() showAll = false;

  gridOptions: GridOptionsExt = {};
  gridApi?: GridApi;

  datasource?: IServerSideDatasource;

  /** Resolved labels, keyed `TypeName|id`. Lives for the life of the dialog: the same config
   *  or program appears on many rows, and a page-at-a-time grid would otherwise re-ask for it
   *  every time the operator scrolls back. */
  private labelCache = new Map<string, string>();
  /** Keys already asked for, so two pages in flight cannot queue the same lookup twice. */
  private inFlight = new Set<string>();
  /** Keys the server looked for and did not find: the row was deleted, or it is outside this
   *  user's account or supplier. Their cache entry says so, and they stay styled as unresolved. */
  private notFound = new Set<string>();

  /** WHICH TYPES CAN BE NAMED. Everything else falls back to a short id, which is why the
   *  fallback is styled as unresolved rather than dressed up to look deliberate. The labels are
   *  built on the server by BaseController.ChangeLogLabels, which knows these same type names.
   *  They are not read through per-type Breeze queries: the grid queries through its caller's
   *  controller, and most controllers expose only some of these types - the rest are 404s. */
  private static readonly RESOLVABLE = new Set([
    'ProductTypeConfig', 'ProductType', 'Program', 'Account',
    'ProgramUserGroup', 'ApprovalTree', 'ProgramProductTypeTag', 'ProximityUser',
  ]);

  constructor(private http: HttpClient) {}

  /** Ids that name a lookup value rather than a row worth resolving. Decoded inline instead. */
  private static readonly ENUM_DECODERS: Record<string, (v: any) => string> = {
    ActiveStatusId: (v) => ActiveStatusEnum[v] ?? String(v),
  };

  ngOnInit() {
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      rowModelType: 'serverSide',
      tooltipShowDelay: 300,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.gridApi && this.filterModel) {
      this.gridApi.setFilterModel(this.filterModel);
    }
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
    const [colDefs, sortModel] = this.getColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);

    this.datasource = AgFns.buildDatasource(() => {
      let q = this.uow.createQuery(ChangeLog).expand('user');
      if (this.accountId) {
        q = q.where({ accountId: this.accountId });
      } else if (this.entity) {
        // query on id and on the entity's foreign keys
        const ors: object[] = [];
        const entityId = this.entity.entityAspect.getKey().values[0];
        ors.push({ entityId: entityId });
        ors.push({ entityId2: entityId });

        // remove fk values that are small integers, because they relate to enums
        const keys = EntityFns.getForeignKeyValues(this.entity).filter((k) => k.length > 5);

        for (const k of keys) {
          ors.push({ entityId: k, entityId2: null });
        }
        q = q.where({ or: ors });
      } else if (this.entityId) {
        // query on either entityId or entityId2, to include related entities with multipart keys
        q = q.where({ or: [{ entityId: this.entityId }, { entityId2: this.entityId }] });
      } else if (!this.showAll) {
        // FAIL CLOSED. A caller that meant to scope this grid and passed an empty id must get
        // nothing back, not the entire ChangeLogs table. Id is an identity column starting at 1,
        // so this matches no row.
        q = q.where({ id: 0 });
      }

      return q;
    },
    // Leave the paging sort key at its default; only the post-load hook is new. returnFn runs
    // at the end of every getRows, so each page resolves its own references and nothing is
    // fetched for rows the operator never scrolls to.
    undefined,
    { returnFn: (rows) => void this.resolvePage(rows as any[]) });

    if (this.gridApi) {
      this.gridApi.setGridOption('serverSideDatasource', this.datasource);
    }

    if (this.filterModel) {
      this.gridApi.setFilterModel(this.filterModel);
    }
  }

  getColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      {
        headerName: 'When',
        field: 'ts',
        ...AgFns.flexWidth(170),
        filter: 'agDateColumnFilter',
        valueFormatter: (p) => ChangeLogGridComponent.formatTs(p.value),
      },
      { field: 'userId', hide: true, filter: 'agTextColumnFilter', ...AgFns.flexWidth(200) },
      { headerName: 'Who', field: 'user.username', ...AgFns.flexWidth(160), filter: 'agTextColumnFilter' },
      {
        headerName: 'Action',
        field: 'logType',
        ...AgFns.flexWidth(110),
        filter: 'agSetColumnFilter',
        valueGetter: (p) => ChangeLogGridComponent.actionOf(p.data?.logType),
        cellClass: (p) => {
          const a = ChangeLogGridComponent.actionOf(p.data?.logType);
          return a === 'Added' ? 'cl-add' : a === 'Deleted' ? 'cl-delete' : a === 'Updated' ? 'cl-update' : '';
        },
      },
      {
        headerName: 'What',
        field: 'entityName',
        ...AgFns.flexWidth(190),
        filter: 'agTextColumnFilter',
        valueGetter: (p) => ChangeLogGridComponent.friendlyName(p.data?.entityName),
        tooltipValueGetter: (p) => p.data?.entityName,
      },
      {
        // THE COLUMN THIS REWRITE EXISTS FOR. A join row like ProgramProductTypeConfig carries
        // nothing but two guids, so the old grid could say a config was deleted and never which
        // one. Resolved asynchronously per page - see resolvePage.
        headerName: 'Subject',
        colId: 'subject',
        ...AgFns.flexWidth(340),
        filter: false,
        sortable: false,
        valueGetter: (p) => this.subjectOf(p.data),
        cellClass: (p) => (this.isResolved(p.data) ? '' : 'cl-unresolved'),
        tooltipValueGetter: (p) => this.subjectOf(p.data),
      },
      {
        headerName: 'Changes',
        colId: 'changes',
        ...AgFns.flexWidth(400),
        filter: false,
        sortable: false,
        valueGetter: (p) => this.changesOf(p.data),
        tooltipValueGetter: (p) => this.changesOf(p.data),
      },
      // The raw material, kept but out of the way. Available from the column menu when a row
      // needs to be argued with, and it is what the SQL in the research scripts reads.
      { headerName: 'Id', field: 'entityId', hide: true, ...AgFns.flexWidth(150), filter: 'agTextColumnFilter' },
      { headerName: 'Id2', field: 'entityId2', hide: true, ...AgFns.flexWidth(150), filter: 'agTextColumnFilter' },
      {
        headerName: 'Raw data',
        field: 'data',
        hide: true,
        filter: 'agTextColumnFilter',
        tooltipValueGetter: (params) => params.data?.data,
        ...AgFns.flexWidth(500),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'ts', sort: 'desc' }];
    return [colDefs, sortModel] as const;
  }

  clearFilters() {
    this.gridApi?.setFilterModel(null);
  }

  // ------------------------------------------------------------------ presentation

  private static formatTs(v: any): string {
    if (!v) return '';
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d.getTime())) return String(v);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
      + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  /** Critical and non-critical collapse to one word: the operator wants to know WHAT happened,
   *  and "critical" describes how the server chose to log it, not what was done. */
  private static actionOf(logType: number | undefined): string {
    switch (logType) {
      case LogTypeEnum.Add:
      case LogTypeEnum.CriticalAdd:
        return 'Added';
      case LogTypeEnum.Update:
      case LogTypeEnum.CriticalUpdate:
        return 'Updated';
      case LogTypeEnum.Delete:
      case LogTypeEnum.CriticalDelete:
        return 'Deleted';
      default:
        return logType === undefined ? '' : LogTypeEnum[logType] ?? String(logType);
    }
  }

  /** 'ProgramProductTypeConfig' -> 'Program Product Type Config'. */
  private static friendlyName(name: string | undefined): string {
    return name ? name.replace(/([a-z0-9])([A-Z])/g, '$1 $2') : '';
  }

  private static isGuid(v: any): boolean {
    return typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  }

  private static shortId(v: any): string {
    return ChangeLogGridComponent.isGuid(v) ? `${String(v).slice(0, 8)}...` : String(v ?? '');
  }

  private static parse(row: any): any {
    if (!row?.data) return null;
    try {
      return JSON.parse(row.data);
    } catch {
      return null;
    }
  }

  /** 'ProductTypeConfigId' -> 'ProductTypeConfig', when that is a type we can name. */
  private static resolvableType(propName: string): string | null {
    if (!propName.endsWith('Id')) return null;
    const typeName = propName.slice(0, -2);
    return ChangeLogGridComponent.RESOLVABLE.has(typeName) ? typeName : null;
  }

  // ------------------------------------------------------------------ subject

  /** Every (type, id) this row refers to, derived from the payload's own property NAMES. That
   *  is what makes this generic: the server writes `ProductTypeConfigId` into the JSON, so the
   *  target type is readable by convention and no per-entity map of id positions is needed. */
  private refsOf(row: any): { key: string; typeName: string; id: string }[] {
    const parsed = ChangeLogGridComponent.parse(row);
    const body = parsed?.before ?? parsed?.after ?? {};
    const out: { key: string; typeName: string; id: string }[] = [];
    const seen = new Set<string>();

    for (const propName of Object.keys(body)) {
      const typeName = ChangeLogGridComponent.resolvableType(propName);
      const value = body[propName];
      if (!typeName || !ChangeLogGridComponent.isGuid(value)) continue;
      const key = `${typeName}|${value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, typeName, id: value });
    }

    // The row's own entity, when the entity name is itself resolvable (Program, Account, ...).
    const own = ChangeLogGridComponent.RESOLVABLE.has(row?.entityName);
    if (own && ChangeLogGridComponent.isGuid(row?.entityId)) {
      const key = `${row.entityName}|${row.entityId}`;
      if (!seen.has(key)) out.push({ key, typeName: row.entityName, id: row.entityId });
    }
    return out;
  }

  /** The refs worth showing. A join row names both of its ends; the parent is usually the form
   *  you are already looking at, so the CHILD reads first. */
  private subjectRefs(row: any) {
    const refs = this.refsOf(row);
    const ownName = row?.entityName;
    return refs.filter((r) => r.typeName !== ownName).concat(refs.filter((r) => r.typeName === ownName));
  }

  private subjectOf(row: any): string {
    if (!row) return '';
    const refs = this.subjectRefs(row);
    if (refs.length === 0) return ChangeLogGridComponent.shortId(row.entityId);
    const parts = refs.map((r) => this.labelCache.get(r.key) ?? ChangeLogGridComponent.shortId(r.id));
    return parts.join('   ·   ');
  }

  private isResolved(row: any): boolean {
    const refs = this.subjectRefs(row);
    return refs.length > 0 && refs.every((r) => this.labelCache.has(r.key) && !this.notFound.has(r.key));
  }

  // ------------------------------------------------------------------ changes

  private changesOf(row: any): string {
    const parsed = ChangeLogGridComponent.parse(row);
    if (!parsed) return '';
    const action = ChangeLogGridComponent.actionOf(row?.logType);

    if (action === 'Updated') {
      // CriticalUpdate writes OriginalValuesMap as `before` and the same KEYS with their new
      // values as `after`, so the key set IS the changed-field list - no diffing needed.
      const after = parsed.after ?? {};
      const before = parsed.before ?? {};
      const parts = Object.keys(after)
        .filter((k) => !k.endsWith('RowVersion'))
        .map((k) => `${ChangeLogGridComponent.friendlyName(k.replace(/Id$/, ''))}: `
          + `${this.showValue(k, before[k])} -> ${this.showValue(k, after[k])}`);
      return parts.join(';   ');
    }

    // Add and Delete carry the WHOLE entity, most of it keys and nulls. The identifying ids are
    // already spelled out in Subject, so only the scalars that say something are listed here.
    const body = parsed.after ?? parsed.before ?? {};
    const parts = Object.keys(body)
      .filter((k) => {
        const v = body[k];
        if (v === null || v === undefined || v === '') return false;
        if (typeof v === 'object') return false;
        if (ChangeLogGridComponent.isGuid(v)) return false;
        return !/RowVersion$/.test(k);
      })
      .map((k) => `${ChangeLogGridComponent.friendlyName(k.replace(/Id$/, ''))}: ${this.showValue(k, body[k])}`);
    return parts.join(';   ');
  }

  private showValue(propName: string, v: any): string {
    if (v === null || v === undefined) return '(none)';
    const decode = ChangeLogGridComponent.ENUM_DECODERS[propName];
    if (decode) return decode(v);
    const typeName = ChangeLogGridComponent.resolvableType(propName);
    if (typeName && ChangeLogGridComponent.isGuid(v)) {
      return this.labelCache.get(`${typeName}|${v}`) ?? ChangeLogGridComponent.shortId(v);
    }
    if (ChangeLogGridComponent.isGuid(v)) return ChangeLogGridComponent.shortId(v);
    if (typeof v === 'boolean') return v ? 'yes' : 'no';
    return String(v);
  }

  // ------------------------------------------------------------------ resolution

  /** Called once per loaded page. Sends every unresolved reference, grouped by TYPE, in ONE
   *  request to the caller's own controller. ChangeLogLabels is on BaseController, so it exists on
   *  every controller that can serve this grid. */
  async resolvePage(rows: any[]): Promise<void> {
    const wanted: Record<string, string[]> = {};
    const asked: { key: string; id: string }[] = [];
    for (const row of rows ?? []) {
      for (const ref of this.refsOf(row)) {
        if (this.labelCache.has(ref.key) || this.inFlight.has(ref.key)) continue;
        this.inFlight.add(ref.key);
        asked.push(ref);
        (wanted[ref.typeName] ??= []).push(ref.id);
      }
    }
    if (asked.length === 0) return;

    try {
      const url = this.uow.manager.serviceName + 'ChangeLogLabels';
      const found = await firstValueFrom(this.http.post<ChangeLogLabel[]>(url, wanted));
      // Compare without case: the server writes guids in lower case, the payload may not.
      const labels = new Map((found ?? []).map((f) => [`${f.TypeName}|${f.Id}`.toLowerCase(), f.Label]));
      for (const { key, id } of asked) {
        const label = labels.get(key.toLowerCase());
        if (label !== undefined) {
          this.labelCache.set(key, label);
        } else {
          // Not returned means it does not exist for this user. Cache that too, so the next
          // page that shows the same id does not ask again.
          this.notFound.add(key);
          this.labelCache.set(key, `${ChangeLogGridComponent.shortId(id)} (not found)`);
        }
      }
    } catch {
      // The request failed, and the HTTP interceptor already reported it. Labels must not break
      // the dialog: those rows keep their short id, and a later page asks again.
    } finally {
      for (const { key } of asked) this.inFlight.delete(key);
    }

    this.gridApi?.refreshCells({ force: true, columns: ['subject', 'changes'] });
  }
}
